import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import AttributeChip from '../attributeChip';
import AsyncListTooltip from '../asyncListTooltip';
import UserAvatar from '../userAvatar';
import Tag from '../tag';
import ConditionalTooltip from '../conditionalTooltip';
import { isArrayEmpty } from '../../../utility/helpers';
import { getPersonFullName } from '../../../utility/uiUtils';
import JobChip from '../jobChip';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    width: '100%',
  },
  label: {
    color: primary.bluish4,
    lineHeight: '20px',
    marginBottom: spacing(1),
  },
  list: {
    display: 'grid',
    gridTemplateColumns: ({ visibleItemsCount, totalItems }) =>
      `repeat(${
        totalItems < visibleItemsCount ? totalItems : visibleItemsCount
      }, minmax(auto, min-content)) ${
        totalItems > visibleItemsCount ? 'minmax(auto, min-content)' : ''
      }`,
    gridColumnGap: spacing(1),
  },
  moreItems: {
    cursor: 'pointer',
    color: primary.bluish1,
    backgroundColor: primary.bluish8,
    fontFamily: 'ProximaNova-Bold',
    lineHeight: '20px',
    padding: spacing(1, 2),
    maxWidth: 41,
    flexShrink: 0,
    textAlign: 'center',
    userSelect: 'none',
  },
  attributesTooltip: {
    width: '100%',
  },
  noItems: {
    lineHeight: '28px',
  },
  fitContentChip: {
    width: 'fit-content',
    maxWidth: '100%',
  },
  userTooltip: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'visible',
  },
  tagItemsList: {
    minHeight: 28,
  },
}));

const ListWithTooltip = ({
  className = null,
  labelClass = null,
  listClass = null,
  moreItemsClass = null,
  customJobChipClass = null,
  label = '',
  organizationUser = null,
  items = [],
  isUserList = false,
  isTagsList = false,
  isJobList = false,
  isReadOnly = false,
  asyncListProps = {},
  userAvatarProps = {},
  visibleItemsCount = 5,
  isItemClickable = () => {},
  onItemClick = () => {},
  onChipSelect = () => {},
}) => {
  const totalItems = items?.length;
  const classes = useStyles({ visibleItemsCount, totalItems });

  const handleUserClick = userId => e => {
    e.preventDefault();
    e.stopPropagation();
    onItemClick(userId);
  };

  const renderTooltipLabel = (item, isClickable) => {
    if (isUserList) {
      return (
        <UserAvatar
          variant="subtitle1"
          user={item}
          clickableCaption={isClickable}
          small
          caption
          {...userAvatarProps}
        />
      );
    }

    if (isTagsList) {
      return (
        <Tag tag={item} color={item?.category?.color} isSelected isSmall />
      );
    }

    if (isJobList) {
      return (
        <JobChip
          customClass={classNames(classes.fitContentChip, customJobChipClass)}
          onSelect={onChipSelect}
          isSelectable
          item={item}
        />
      );
    }

    return (
      <AttributeChip
        customClass={classes.fitContentChip}
        name={item.name}
        color={item.color}
      />
    );
  };

  const renderItem = (item, index) => {
    if (isUserList) {
      return (
        <ConditionalTooltip
          key={`user_${item.id}_item_${index}`}
          className={classes.userTooltip}
          message={getPersonFullName(item)}
          addTooltip
        >
          <UserAvatar
            user={item}
            isOrganizationUser={organizationUser?.id === item.id}
            clickableCaption={!isReadOnly && isItemClickable(item)}
            onClickHandler={handleUserClick(item.id)}
            small
            {...userAvatarProps}
          />
        </ConditionalTooltip>
      );
    }

    if (isTagsList) {
      return (
        <Tag
          key={`tag_${item.id}_item_${index}`}
          tag={item}
          color={item?.category?.color}
          isSelected
          isSmall
        />
      );
    }

    if (isJobList) {
      return (
        <JobChip
          customClass={customJobChipClass}
          onSelect={onChipSelect}
          isSelectable
          key={`job_${item.id}_item_${index}`}
          item={item}
          jobNumber={totalItems}
        />
      );
    }

    return (
      <AttributeChip
        key={`attribute_${item.id}_item_${index}`}
        name={item.name}
        color={item.color}
        attributeNumber={totalItems}
      />
    );
  };

  return (
    <div className={classNames(classes.main, className)}>
      {label && (
        <Typography
          variant="body2"
          className={classNames(classes.label, labelClass)}
        >
          {label}
        </Typography>
      )}
      {isArrayEmpty(items) ? (
        <Typography variant="subtitle2" className={classes.noItems}>
          -
        </Typography>
      ) : (
        <div
          className={classNames(
            classes.list,
            { [classes.tagItemsList]: isTagsList },
            listClass
          )}
        >
          {items
            .slice(0, visibleItemsCount)
            .map((item, index) => renderItem(item, index))}
          {totalItems > visibleItemsCount && (
            <AsyncListTooltip
              labelData={items.slice(visibleItemsCount)}
              renderLabelItem={renderTooltipLabel}
              isItemClickable={isItemClickable}
              onItemClickHandler={onItemClick}
              {...asyncListProps}
            >
              <Typography
                variant="body2"
                className={classNames(classes.moreItems, moreItemsClass)}
              >
                {`+${totalItems - visibleItemsCount}`}
              </Typography>
            </AsyncListTooltip>
          )}
        </div>
      )}
    </div>
  );
};

ListWithTooltip.propTypes = {
  className: PropTypes.string,
  labelClass: PropTypes.string,
  listClass: PropTypes.string,
  moreItemsClass: PropTypes.string,
  customJobChipClass: PropTypes.string,
  label: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  isUserList: PropTypes.bool,
  isTagsList: PropTypes.bool,
  isJobList: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  asyncListProps: PropTypes.shape({}),
  organizationUser: PropTypes.object,
  userAvatarProps: PropTypes.shape({}),
  visibleItemsCount: PropTypes.number,
  isItemClickable: PropTypes.func,
  onItemClick: PropTypes.func,
  onChipSelect: PropTypes.func,
};

export default ListWithTooltip;
